import { memo, useCallback, useMemo } from 'react';

import { type SegmentedControlOption } from '@air/component-segmented-control';

import { SegmentedControlRow } from '~/components/ShareModal/components/SegmentedControlRow';

export type AssetVersionControlRowProps = {
  /**
   * Determines the row + option labels:
   * - 'multi-asset' (board / collection): "Asset version control" | "Latest only" / "All versions"
   * - 'single-asset' (asset modal): "Version control" | "This version" / "All versions"
   */
  variant: 'multi-asset' | 'single-asset';
  canViewAllVersions: boolean;
  disabled?: boolean;
  isLoading: boolean;
  onChange: (canViewAllVersions: boolean) => void;
  'data-testid'?: string;
};

export const AssetVersionControlRow = memo(
  ({
    variant,
    canViewAllVersions,
    disabled = false,
    isLoading,
    onChange,
    'data-testid': dataTestId,
  }: AssetVersionControlRowProps) => {
    const selectedValue = useMemo(() => (canViewAllVersions ? 'all-versions' : 'latest-or-this'), [canViewAllVersions]);

    const options = useMemo((): SegmentedControlOption[] => {
      // The falsy segment uses a single shared id ('latest-or-this') across both variants so the
      // same select handler works regardless of which label the user sees.
      const falsyOption: SegmentedControlOption =
        variant === 'multi-asset'
          ? {
              label: 'Latest only',
              value: 'latest-or-this',
              id: 'latest-or-this',
              tooltip: 'Latest only',
              disabled: disabled || isLoading,
            }
          : {
              label: 'This version',
              value: 'latest-or-this',
              id: 'latest-or-this',
              tooltip: 'This version',
              disabled: disabled || isLoading,
            };

      return [
        falsyOption,
        {
          label: 'All versions',
          value: 'all-versions',
          id: 'all-versions',
          tooltip: 'All versions',
          disabled: disabled || isLoading,
        },
      ];
    }, [disabled, isLoading, variant]);

    const selectedOption = useMemo(
      () => options.find((option) => option.value === selectedValue) ?? options[0],
      [options, selectedValue],
    );

    const handleSelect = useCallback(
      (option: SegmentedControlOption) => {
        if (option.value === 'all-versions') {
          onChange(true);
        } else {
          onChange(false);
        }
      },
      [onChange],
    );

    return (
      <SegmentedControlRow
        data-testid={dataTestId}
        label={variant === 'multi-asset' ? 'Asset version control' : 'Version control'}
        onSelect={handleSelect}
        options={options}
        selectedOption={selectedOption}
      />
    );
  },
);

AssetVersionControlRow.displayName = 'AssetVersionControlRow';
